import { useState } from "react";
import { UseFormReturn } from "react-hook-form";

import { Alert } from "../base/Alert";
import { Button } from "../base/Button";
import Card from "../shared/Card/Card";
import Form from "../base/Form/Form";
import { FormErrors } from "../base/Form/FormErrors";
import { FormSelect } from "../base/Form/FormSelect";
import { FormTextField } from "../base/Form/FormTextField";
import { FormToggle } from "../base/Form/FormToggle";
import Modal from "../../layouts/Modal";
import LoadingWheel from "../shared/LoadingWheel";
import { errorResolver } from "../../utils/errorResolver";
import useCustomerSsoConfig from "../../data/CustomerSsoConfig/useCustomerSsoConfig";
import useCustomerSsoConfigMutations from "../../data/CustomerSsoConfig/useCustomerSsoConfigMutations";
import {
  CreateUpdateSsoConfigRequest,
  SsoProvider,
  SsoProviderLookup,
  SsoUserProvisioningMode,
  SsoUserProvisioningModeLookup,
} from "../../models/CustomerSsoConfig/CustomerSsoConfig";

interface CustomerSsoConfigSectionProps {
  customerId: string;
}

const REDIRECT_URI =
  "https://sso-api.recyclingsoftware.co.uk/v1/auth/entra/callback";

export function CustomerSsoConfigSection({
  customerId,
}: CustomerSsoConfigSectionProps) {
  const { ssoConfig } = useCustomerSsoConfig(customerId);
  const { createOrUpdate, remove } = useCustomerSsoConfigMutations(customerId);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const handleSave = (data: CreateUpdateSsoConfigRequest) => {
    const payload: CreateUpdateSsoConfigRequest = {
      ...data,
      provider: Number(data.provider),
      userProvisioningMode: Number(data.userProvisioningMode),
    };

    createOrUpdate.mutate(payload, {
      onSuccess: () => setModalOpen(false),
    });
  };

  const handleDelete = () => {
    remove.mutate(undefined, {
      onSuccess: () => setConfirmDeleteOpen(false),
    });
  };

  return (
    <>
      <Card title="Single Sign-On" bodyClassName="p-4">
        {ssoConfig.isLoading ? (
          <div className="py-4">
            <LoadingWheel />
          </div>
        ) : ssoConfig.isError ? (
          <div className="p-4">
            <Alert
              severity="error"
              title="Error loading SSO config"
              message={errorResolver(ssoConfig.error)}
            />
          </div>
        ) : ssoConfig.data ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">Provider</p>
                <p className="text-sm text-gray-900">
                  {SsoProviderLookup[ssoConfig.data.provider]}
                </p>
              </div>
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  ssoConfig.data.enabled
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                {ssoConfig.data.enabled ? "Enabled" : "Disabled"}
              </span>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Tenant ID</p>
              <p className="text-sm text-gray-900 font-mono">
                {ssoConfig.data.tenantId}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Client ID</p>
              <p className="text-sm text-gray-900 font-mono">
                {ssoConfig.data.clientId}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">
                Client Secret
              </p>
              <p className="text-sm text-gray-900">
                {ssoConfig.data.hasClientSecret ? "********" : "Not set"}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Auto-Redirect</p>
              <p className="text-sm text-gray-900">
                {ssoConfig.data.autoRedirect ? "Yes" : "No"}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">
                User Provisioning
              </p>
              <p className="text-sm text-gray-900">
                {
                  SsoUserProvisioningModeLookup[
                    ssoConfig.data.userProvisioningMode
                  ]
                }
              </p>
            </div>
            <div className="flex space-x-2 pt-2">
              <Button onClick={() => setModalOpen(true)}>Edit</Button>
              <Button
                variant="outlined"
                onClick={() => setConfirmDeleteOpen(true)}
              >
                Remove
              </Button>
            </div>
          </div>
        ) : (
          <div className="py-4 text-center">
            <p className="text-sm text-gray-500 mb-4">
              No SSO configuration set up for this customer.
            </p>
            <Button onClick={() => setModalOpen(true)}>Configure SSO</Button>
          </div>
        )}
      </Card>

      {/* Configure/Edit Modal */}
      <Modal open={modalOpen} setOpen={setModalOpen} width="!w-2/3 !max-w-4xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left: Form */}
          <div>
            <div className="border-b">
              <h2 className="text-lg font-medium leading-6 mb-4 text-gray-900">
                {ssoConfig.data ? "Edit" : "Configure"} SSO
              </h2>
            </div>
            <Form<CreateUpdateSsoConfigRequest>
              defaultValues={
                ssoConfig.data
                  ? {
                      provider: ssoConfig.data.provider,
                      tenantId: ssoConfig.data.tenantId,
                      clientId: ssoConfig.data.clientId,
                      clientSecret: "",
                      enabled: ssoConfig.data.enabled,
                      autoRedirect: ssoConfig.data.autoRedirect,
                      userProvisioningMode:
                        ssoConfig.data.userProvisioningMode,
                    }
                  : {
                      provider: SsoProvider.EntraId,
                      enabled: false,
                      autoRedirect: false,
                      userProvisioningMode:
                        SsoUserProvisioningMode.PreProvision,
                    }
              }
              onSubmit={handleSave}
              className="grid grid-cols-1 gap-y-4 mt-4"
            >
              {({
                register,
                control,
              }: UseFormReturn<CreateUpdateSsoConfigRequest>) => (
                <>
                  <FormErrors />

                  <FormSelect
                    {...register("provider")}
                    label="Provider"
                    fullWidth
                  >
                    <option value={SsoProvider.EntraId}>
                      Microsoft Entra ID
                    </option>
                  </FormSelect>

                  <FormTextField
                    {...register("tenantId", { required: true })}
                    label="Tenant ID"
                    fullWidth
                  />

                  <FormTextField
                    {...register("clientId", { required: true })}
                    label="Client ID"
                    fullWidth
                  />

                  <div>
                    <FormTextField
                      {...register("clientSecret")}
                      label="Client Secret"
                      type="password"
                      fullWidth
                    />
                    {ssoConfig.data?.hasClientSecret && (
                      <p className="text-xs text-gray-500 mt-1">
                        Leave blank to keep the existing secret.
                      </p>
                    )}
                  </div>

                  <FormSelect
                    {...register("userProvisioningMode")}
                    label="User Provisioning Mode"
                    fullWidth
                  >
                    <option value={SsoUserProvisioningMode.AutoProvision}>
                      Auto-provision (create users on first login)
                    </option>
                    <option value={SsoUserProvisioningMode.PreProvision}>
                      Pre-provision (users must exist in Wilma)
                    </option>
                  </FormSelect>

                  <FormToggle
                    {...register("enabled")}
                    control={control}
                    toggleProps={{ label: "Enabled" }}
                  />

                  <FormToggle
                    {...register("autoRedirect")}
                    control={control}
                    toggleProps={{ label: "Auto-Redirect" }}
                  />
                  <p className="text-xs text-gray-500 -mt-3">
                    When enabled, users are automatically redirected to Microsoft login.
                    When disabled, the standard login form is shown with a "Login with Microsoft" button.
                  </p>

                  <div className="bg-gray-50 rounded p-3 mt-2">
                    <p className="text-xs font-medium text-gray-700 mb-1">
                      Redirect URI (provide to customer)
                    </p>
                    <code className="text-xs text-gray-900 break-all select-all">
                      {REDIRECT_URI}
                    </code>
                  </div>

                  <div className="flex space-x-2 mt-2">
                    <Button type="submit">Save</Button>
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={() => setModalOpen(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </>
              )}
            </Form>
          </div>

          {/* Right: How-to Guide */}
          <div className="border-l pl-6">
            <div className="border-b">
              <h2 className="text-lg font-medium leading-6 mb-4 text-gray-900">
                Setup Guide
              </h2>
            </div>
            <div className="mt-4 max-h-[520px] overflow-y-auto pr-2 space-y-5 text-sm text-gray-700">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  1. Customer creates an Azure App Registration
                </h3>
                <p className="mb-2">
                  The customer's IT admin needs to do the following in their{" "}
                  <span className="font-medium">Azure Portal</span>:
                </p>
                <ol className="list-decimal list-outside ml-4 space-y-1.5">
                  <li>
                    Go to <span className="font-medium">Microsoft Entra ID &gt; App registrations &gt; New registration</span>
                  </li>
                  <li>
                    Name the app (e.g. "Wilma SSO")
                  </li>
                  <li>
                    Set <span className="font-medium">Supported account types</span> to
                    "Accounts in this organizational directory only"
                  </li>
                  <li>
                    Add a <span className="font-medium">Redirect URI</span> (Web):{" "}
                    <code className="bg-gray-100 px-1 py-0.5 rounded text-xs break-all select-all">
                      {REDIRECT_URI}
                    </code>
                  </li>
                  <li>Click <span className="font-medium">Register</span></li>
                </ol>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  2. Customer collects the credentials
                </h3>
                <ol className="list-decimal list-outside ml-4 space-y-1.5">
                  <li>
                    From the app's <span className="font-medium">Overview</span> page, copy the{" "}
                    <span className="font-medium">Application (client) ID</span> and{" "}
                    <span className="font-medium">Directory (tenant) ID</span>
                  </li>
                  <li>
                    Go to <span className="font-medium">Certificates &amp; secrets &gt; New client secret</span>,
                    create one, and copy the <span className="font-medium">Value</span> (only shown once)
                  </li>
                </ol>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  3. Customer checks API permissions
                </h3>
                <p>
                  Under <span className="font-medium">API permissions</span>, ensure the following
                  Microsoft Graph delegated permissions are granted:
                </p>
                <ul className="list-disc list-outside ml-4 mt-1.5 space-y-1">
                  <li><code className="bg-gray-100 px-1 py-0.5 rounded text-xs">openid</code></li>
                  <li><code className="bg-gray-100 px-1 py-0.5 rounded text-xs">profile</code></li>
                  <li><code className="bg-gray-100 px-1 py-0.5 rounded text-xs">email</code></li>
                </ul>
                <p className="mt-1.5 text-gray-500 text-xs">
                  These are typically granted by default.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  4. Enter the details here
                </h3>
                <p>
                  The customer provides you with three values:
                </p>
                <ul className="list-disc list-outside ml-4 mt-1.5 space-y-1">
                  <li><span className="font-medium">Tenant ID</span> &mdash; their Azure AD directory ID</li>
                  <li><span className="font-medium">Client ID</span> &mdash; the app registration's application ID</li>
                  <li><span className="font-medium">Client Secret</span> &mdash; the secret value they generated</li>
                </ul>
                <p className="mt-1.5">
                  Enter them in the form on the left.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  5. Choose a provisioning mode
                </h3>
                <ul className="list-disc list-outside ml-4 space-y-1.5">
                  <li>
                    <span className="font-medium">Auto-provision</span> &mdash; users are
                    created automatically in Wilma on their first Entra login
                    (assigned the staff role by default). Best for customers who
                    manage users entirely in Entra.
                  </li>
                  <li>
                    <span className="font-medium">Pre-provision</span> &mdash; users must
                    already exist in Wilma with a matching email address. Entra
                    only verifies their identity. Best for tighter access control.
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  6. Enable and save
                </h3>
                <p>
                  Toggle <span className="font-medium">Enabled</span> on when
                  ready, then click <span className="font-medium">Save</span>.
                  If <span className="font-medium">Auto-Redirect</span> is on,
                  users are sent straight to Microsoft login. If off, the
                  standard username/password form is shown with a
                  "Login with Microsoft" button alongside it.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Confirm Delete Modal */}
      <Modal
        open={confirmDeleteOpen}
        setOpen={setConfirmDeleteOpen}
        width="!w-1/4"
      >
        <div>
          <h2 className="text-lg font-medium leading-6 mb-4 text-gray-900">
            Remove SSO Configuration
          </h2>
          <p className="text-sm text-gray-500 mb-6">
            Are you sure you want to remove the SSO configuration for this
            customer? Users will no longer be able to sign in with Microsoft
            Entra ID.
          </p>
          <div className="flex space-x-2">
            <Button onClick={handleDelete}>Remove</Button>
            <Button
              variant="outlined"
              onClick={() => setConfirmDeleteOpen(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
